import React, { useState } from "react";
import {
  Badge,
  Button,
  Popover,
  List,
  Typography,
  Space,
  Divider,
  Spin,
} from "antd";
import {
  BellOutlined,
  MessageOutlined,
  CheckCircleOutlined,
  SettingOutlined,
  DollarOutlined,
  CommentOutlined,
  CalendarOutlined,
  FileTextOutlined,
  SecurityScanOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { convertDateToUserTimezone } from "../Components/Utility";
import { DateTimeFormat } from "../Components/Constant";
import dayjs from "dayjs";

const { Text } = Typography;

// --- Map icons by type ---
const getNotificationIcon = (type) => {
  const iconMap = {
    message: <MessageOutlined style={{ color: "#1890ff" }} />,
    task: <CheckCircleOutlined style={{ color: "#52c41a" }} />,
    system: <SettingOutlined style={{ color: "#722ed1" }} />,
    payment: <DollarOutlined style={{ color: "#13c2c2" }} />,
    comment: <CommentOutlined style={{ color: "#eb2f96" }} />,
    meeting: <CalendarOutlined style={{ color: "#fa541c" }} />,
    file: <FileTextOutlined style={{ color: "#faad14" }} />,
    security: <SecurityScanOutlined style={{ color: "#f5222d" }} />,
    welcome: <UserOutlined style={{ color: "#52c41a" }} />,
    account: <UserOutlined style={{ color: "#1890ff" }} />,
  };
  return iconMap[type] || <BellOutlined style={{ color: "#1677ff" }} />;
};

const Notification = ({
  notifications,
  onMarkAllAsRead,
  onMarkAsRead,
  loading,
}) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // ✅ Only unread, sorted by latest first
  const unreadNotifications = [...notifications]
    .filter((n) => !n.is_read)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const unreadCount = unreadNotifications.length;

  const handleNotificationClick = (item) => {
    onMarkAsRead(item._id);
    setOpen(false);
    if (item.record_id) {
      navigate(`/record/${item.object_name}/${item.record_id}`);
    }
  };

  const handleViewAll = () => {
    navigate("/notifications");
    setOpen(false);
  };

  const notificationContent = (
    <div style={{ width: 320, maxHeight: 400, overflowY: "auto" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <Text strong style={{ fontSize: 16 }}>
          Notifications
        </Text>
        <Button
          type="link"
          size="small"
          onClick={onMarkAllAsRead}
          style={{ padding: 0 }}
        >
          Mark all as read
        </Button>
      </div>

      <Divider style={{ margin: "8px 0" }} />

      {/* List of unread notifications only */}
      <List
        itemLayout="horizontal"
        dataSource={unreadNotifications}
        locale={{
          emptyText: (
            <div style={{ textAlign: "center", padding: "12px 0" }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                No new notifications
              </Text>
            </div>
          ),
        }}
        renderItem={(item) => (
          <List.Item
            style={{
              padding: "5px 0",
              backgroundColor: "#f6f8fa",
              borderRadius: 6,
              marginBottom: 4,
              paddingLeft: 8,
              cursor: "pointer",
              transition: "all 0.2s",
            }}
            onClick={() => handleNotificationClick(item)}
          >
            <List.Item.Meta
              avatar={getNotificationIcon(item.notification_type)}
              title={
                <Text strong style={{ fontSize: 12 }}>
                  {item.notification_type}
                </Text>
              }
              description={
                <Space direction="vertical" size={2} style={{ width: "100%" }}>
                  {/* ✅ Object name */}
                  {item.object_name && (
                    <Text type="secondary" style={{ fontSize: 10, fontWeight: 500 }}>
                      {item.object_name}
                    </Text>
                  )}

                  {/* ✅ Message */}
                  <Text type="secondary" style={{ fontSize: 10 }}>
                    {item.message}
                  </Text>
                  
                  {/* Selected Fields / Record Details */}
                  {item.selected_fields && Object.keys(item.selected_fields).length > 0 && (
                    <div style={{ maxHeight: 80, overflowY: 'auto', paddingLeft: 4 }}>
                      {Object.entries(item.selected_fields).map(([key, value]) => (
                        <div key={key}>
                          <strong>{key}: </strong>{String(value)}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* ✅ Date */}
                  <Text type="secondary" style={{ fontSize: 9 }}>
                    {item.created_at && dayjs(item.created_at).isValid()
                      ? convertDateToUserTimezone(item.created_at, DateTimeFormat)
                      : ""}
                  </Text>
                </Space>
              }
            />
          </List.Item>
        )}
      />


      <Divider style={{ margin: "8px 0" }} />

      {/* Footer */}
      <div style={{ textAlign: "center" }}>
        <Button
          type="link"
          size="small"
          onClick={handleViewAll}
          style={{ padding: 0 }}
        >
          View All Notifications
        </Button>
      </div>
    </div>
  );

  return (
    <Popover
      content={notificationContent}
      title={null}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
    >
      <Badge
        count={loading ? <Spin size="small" /> : unreadCount}
        offset={[-6, 6]}
        style={{
          fontSize: 8,
          height: 14,
          minWidth: 14,
          lineHeight: "14px",
        }}
      >
        <Button
          type="text"
          icon={<BellOutlined />}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 8,
            border: "1px solid #d1d5db",
            backgroundColor: "#fff",
            marginRight: "8px",
          }}
        />
      </Badge>
    </Popover>
  );
};

export default Notification;
